import React, {FC, useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {
    AgreementCreateRequest,
    AgreementStructure,
    AgreementStudentStructure,
    AgreementTeacherStructure,
    LearningLanguage,
    LearningLanguageNameMap,
    LessonFormat,
    LessonFormatNameMap
} from '../../shared/types/agreement';
import {createAgreementFetch} from '../../app/axios/agreementFetches';
import HLFormInput from '../../shared/components/input/HLFormInput';
import HLFormSelect from '../../shared/components/select/HLFormSelect';
import HLButton, {HLButtonStyleType} from '../../shared/components/button/HLButton';
import HLErrorMessage from '../../shared/components/error-message/HLErrorMessage';
import HLModal, {HLModalSizeType} from '../../shared/components/modal/HLModal';
import {processFormServerError} from '../../shared/utils/processFormServerError';
import TeacherSelector from '../../components/selector/TeacherSelector';
import StudentSelector from '../../components/selector/StudentSelector';

const learningLanguageOptions = Object.entries(LearningLanguageNameMap).map(([value, label]) => ({
    value,
    label
}));

const lessonFormatOptions = Object.entries(LessonFormatNameMap).map(([value, label]) => ({
    value,
    label
}));

interface CreateAgreementFormData {
    name: string;
    agreementDate: string;
    learningLanguage: LearningLanguage;
    lessonFormat: LessonFormat;
    selectedTeacher: AgreementTeacherStructure;
    selectedStudents: AgreementStudentStructure[];
}

interface CreateAgreementFormProps {
    open: boolean;
    setOpen: (value: boolean) => void;
    onSuccess: (agreement: AgreementStructure) => void;
}

const CreateAgreementModal: FC<CreateAgreementFormProps> = ({
                                                                open,
                                                                setOpen,
                                                                onSuccess
                                                            }) => {

    const {
        register,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        setValue,
        watch,
        formState: {errors, isSubmitting}
    } = useForm<CreateAgreementFormData>({
        mode: "onChange"
    })

    useEffect(() => {
        if (open) reset();
    }, [open, reset]);

    const createAgreement = async (data: CreateAgreementFormData) => {
        try {
            const request: AgreementCreateRequest = {
                name: data.name,
                agreementDate: data.agreementDate,
                learningLanguage: data.learningLanguage,
                lessonFormat: data.lessonFormat,
                teacherId: data.selectedTeacher.id,
                teacherRate: data.selectedTeacher.teacherRate,
                students: data.selectedStudents.map(selectedStudent => ({
                    studentId: selectedStudent.id,
                    lessonPrice: selectedStudent.lessonPrice,
                    availableLessons: selectedStudent.availableLessons
                }))
            };
            const agreement = await createAgreementFetch(request);

            closeModal()
            onSuccess(agreement)
        } catch (err: any) {
            processFormServerError(err, setError);
        }
    }

    const closeModal = () => {
        reset()
        setOpen(false)
    }

    return (
        <HLModal open={open} onClose={closeModal} title="Создать договор" sizeType={HLModalSizeType.MEDIUM}>
            <form onSubmit={handleSubmit(createAgreement)} className="columnContainer">
                <HLErrorMessage error={errors.root?.message}/>

                <HLFormInput
                    id="name"
                    {...register('name', {
                        required: 'Название договора обязательно'
                    })}
                    placeholder="Название договора"
                    error={errors.name?.message}
                />

                <HLFormInput
                    id="agreementDate"
                    type="date"
                    {...register('agreementDate', {
                        required: 'Дата договора обязательна'
                    })}
                    error={errors.agreementDate?.message}
                />

                <HLFormSelect
                    id="learningLanguage"
                    {...register('learningLanguage', {
                        required: 'Язык обучения обязателен'
                    })}
                    value={watch('learningLanguage') || ''}
                    options={[
                        {value: '', label: 'Язык обучения'},
                        ...learningLanguageOptions
                    ]}
                    error={errors.learningLanguage?.message}
                />

                <HLFormSelect
                    id="lessonFormat"
                    {...register('lessonFormat', {
                        required: 'Формат занятий обязателен'
                    })}
                    value={watch('lessonFormat') || ''}
                    options={[
                        {value: '', label: 'Формат занятий'},
                        ...lessonFormatOptions
                    ]}
                    error={errors.lessonFormat?.message}
                />

                <TeacherSelector
                    register={register}
                    setError={setError}
                    clearErrors={clearErrors}
                    setValue={setValue}
                    watch={watch}
                    errors={errors}
                />

                <StudentSelector
                    register={register}
                    setError={setError}
                    clearErrors={clearErrors}
                    setValue={setValue}
                    watch={watch}
                    errors={errors}
                />

                <div className="inlineContainer">
                    <HLButton
                        type="submit"
                        disabled={isSubmitting}
                    >
                        Создать
                    </HLButton>

                    <HLButton
                        type="button"
                        onClick={closeModal}
                        styleType={HLButtonStyleType.GRAY}
                    >
                        Отмена
                    </HLButton>
                </div>
            </form>


        </HLModal>
    );
};

export default CreateAgreementModal; 